import { remCSS } from '@gimlite/common';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';
import './zone.component.scss';

export declare namespace ZoneType {
  type Props = {
    className?: string;
    children: React.ReactNode;
    gapY?: number;
    gapX?: number;
    area?: string;
    zones?: string[][];
    rows?: string[];
    columns?: string[];
    vertical?: 'flex-start' | 'center' | 'flex-end';
    horizontal?: 'flex-start' | 'center' | 'flex-end';
    padding?: {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    };
    overflow?: 'hidden' | 'visible' | 'scroll' | 'initial' | 'auto';
  };
}

export const Zone = observer(
  ({
    className = '',
    zones = [],
    rows = [],
    columns = [],
    gapY = 16,
    gapX = 16,
    vertical,
    horizontal,
    padding = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    overflow = 'auto',
    children,
  }: ZoneType.Props) => {
    return (
      <div
        className={classNames('zone', className)}
        style={{
          gap: `${remCSS(gapY)} ${remCSS(gapX)}`,
          gridTemplateAreas: zones.reduce(
            (acc: string, array) => `${acc} "${array.join(' ')}"`,
            ``,
          ),
          justifyContent: vertical,
          alignItems: horizontal,
          paddingRight: remCSS(padding.right ?? 0),
          paddingLeft: remCSS(padding.left ?? 0),
          paddingTop: remCSS(padding.top ?? 0),
          paddingBottom: remCSS(padding.bottom ?? 0),
          gridTemplateRows: rows ? rows.join(' ') : undefined,
          gridTemplateColumns: columns ? columns.join(' ') : undefined,
          overflow,
        }}
      >
        {children}
      </div>
    );
  },
);

export declare namespace ZoneAreaType {
  type Props = {
    className?: string;
    children: React.ReactNode;
    area?: string;
    height?: 'min-content' | 'max-content';
    overflowY?: 'hidden' | 'visible' | 'scroll' | 'initial';
    overflowX?: 'hidden' | 'visible' | 'scroll' | 'initial';
  };
}

export const ZoneArea = observer(
  ({
    className,
    area,
    height,
    children,
    overflowY = 'hidden',
    overflowX = 'hidden',
  }: ZoneAreaType.Props) => {
    if (!children) return null;

    const childrenClear = useMemo(
      (): React.ReactNode[] =>
        Array.isArray(children) ? children.filter(Boolean) : [children],
      [children],
    );

    if (childrenClear.length !== 1) return null;

    const childrenOnly = useMemo(
      () => React.Children.only(childrenClear[0]),
      [childrenClear],
    );

    useEffect(() => {
      if (childrenClear.length > 1) {
        console.error(
          "Il faut un seul enfant pour utiliser ZoneArea,l'autre est automariquement masqué",
        );
      }
    }, [children]);

    if (!childrenOnly) return null;

    return (
      <div
        className={classNames(`zonearea`, className)}
        style={{ gridArea: area, height, overflowY, overflowX }}
      >
        {childrenOnly}
      </div>
    );
  },
);
